import type { LearningExercise } from "../types";

// Hagen BGB-AT Vertiefung — Flashcards, Orderings, Clozes zu BGB AT-Instituten.
// Abdeckung: Geschäftsfähigkeit (§§ 104/105/107/108/110), Anfechtung (§§ 119 II/121/122/123/124/143),
// Auslegung (§§ 133/157 + falsa demonstratio), Form/Nichtigkeit (§§ 125/134/138/139/140),
// Bedingung/Befristung (§§ 158/162/163), Verjährung (§§ 195/199/214), Vertragsschluss (§§ 130/145–147/154/155).
// Stellvertretung §§ 164–181 ist bereits als hj-stellv-001..014 (kind "single") vollständig vorhanden — NICHT wiederholt.
// Bestehende hj-card-001..006, hj-order-001..002, hj-cloze-001..002 werden NICHT dupliziert.
// reviewStatus "draft" für alle Items — Freigabe macht question-reviewer + Vincent.
export const hagenBgbAtVertiefung: LearningExercise[] = [

  // ─── FLASHCARDS hj-card-007 … hj-card-030 ───────────────────────────────

  {
    id: "hj-card-007",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Geschäftsfähigkeit",
    title: "Geschäftsunfähigkeit: § 104 / § 105 BGB",
    prompt: "Wer ist geschäftsunfähig (§ 104 BGB) und welche Rechtsfolge ordnet § 105 BGB an?",
    options: [],
    answers: [],
    explanation:
      "§ 104 BGB nennt zwei Fallgruppen: (1) wer das siebente Lebensjahr nicht vollendet hat, und (2) wer sich in einem die freie Willensbestimmung dauerhaft ausschließenden Zustand krankhafter Störung der Geistestätigkeit befindet. Rechtsfolge: § 105 Abs. 1 BGB ordnet die Nichtigkeit einer Willenserklärung eines Geschäftsunfähigen an. § 105 Abs. 2 BGB stellt klar, dass auch eine WE, die im Zustand vorübergehender Bewusstlosigkeit oder vorübergehender Störung der Geistestätigkeit abgegeben wird, nichtig ist.",
    sourceIds: ["bgb104", "bgb105"],
    sources: [
      { id: "bgb104", label: "§ 104 BGB", url: "https://www.gesetze-im-internet.de/bgb/__104.html" },
      { id: "bgb105", label: "§ 105 BGB", url: "https://www.gesetze-im-internet.de/bgb/__105.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["bgb-at", "geschaeftsfaehigkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-008",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Geschäftsfähigkeit",
    title: "Lediglich rechtlicher Vorteil (§ 107 BGB)",
    prompt:
      "Was versteht man unter einem Geschäft, das dem Minderjährigen lediglich einen rechtlichen Vorteil bringt (§ 107 BGB), und welche Rechtsfolge hat das?",
    options: [],
    answers: [],
    explanation:
      "§ 107 BGB: Der Minderjährige bedarf zur Abgabe einer Willenserklärung, durch die er nicht lediglich einen rechtlichen Vorteil erlangt, der Einwilligung seines gesetzlichen Vertreters. 'Lediglich rechtlich vorteilhaft' bedeutet, dass dem Minderjährigen durch das Geschäft ausschließlich Rechte zuwachsen, aber keinerlei rechtliche Verpflichtungen entstehen (rein schenkungsweise Zuwendung, § 516 BGB). Wirtschaftliche Vorteile genügen nicht; maßgeblich ist die juristische Bilanz. Liegt nur ein rechtlicher Vorteil vor, ist die WE des Minderjährigen allein wirksam.",
    sourceIds: ["bgb107"],
    sources: [
      { id: "bgb107", label: "§ 107 BGB", url: "https://www.gesetze-im-internet.de/bgb/__107.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["bgb-at", "geschaeftsfaehigkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-009",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Geschäftsfähigkeit",
    title: "Schwebende Unwirksamkeit und Genehmigung (§ 108 BGB)",
    prompt:
      "Was bedeutet 'schwebende Unwirksamkeit' nach § 108 Abs. 1 BGB und welche Frist gilt nach § 108 Abs. 2 BGB?",
    options: [],
    answers: [],
    explanation:
      "§ 108 Abs. 1 BGB: Schließt der Minderjährige ohne Einwilligung des gesetzlichen Vertreters einen Vertrag, so hängt die Wirksamkeit von der Genehmigung des Vertreters ab. Der Vertrag ist bis zur Genehmigung schwebend unwirksam — weder voll wirksam noch endgültig nichtig. § 108 Abs. 2 S. 1 BGB: Der andere Teil kann den Vertreter auffordern, über die Genehmigung zu erklären. § 108 Abs. 2 S. 2 BGB: Die Genehmigung kann nur bis zum Ablauf von zwei Wochen nach dem Empfang der Aufforderung erklärt werden; wird sie nicht erklärt, gilt sie als verweigert.",
    sourceIds: ["bgb108"],
    sources: [
      { id: "bgb108", label: "§ 108 BGB", url: "https://www.gesetze-im-internet.de/bgb/__108.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["bgb-at", "geschaeftsfaehigkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-010",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Geschäftsfähigkeit",
    title: "Taschengeldparagraph (§ 110 BGB)",
    prompt:
      "Wann ist ein Vertrag des Minderjährigen nach § 110 BGB (Taschengeldparagraph) wirksam?",
    options: [],
    answers: [],
    explanation:
      "§ 110 BGB: Ein vom Minderjährigen ohne Einwilligung des gesetzlichen Vertreters geschlossener Vertrag gilt von Anfang an als wirksam, wenn der Minderjährige die vertragsmäßige Leistung mit Mitteln bewirkt, die ihm zu diesem Zweck oder zu freier Verfügung von dem Vertreter oder mit dessen Zustimmung von einem Dritten überlassen worden sind. Voraussetzungen: (1) überlassene Mittel (Taschengeld oder zweckgebunden), (2) vollständige Bewirkung der Leistung (bloßes Angebot reicht nicht, Ratenkauf bei noch ausstehender letzter Rate scheitert hier), (3) vollständige Erfüllung vor oder bei Vertragsschluss.",
    sourceIds: ["bgb110"],
    sources: [
      { id: "bgb110", label: "§ 110 BGB", url: "https://www.gesetze-im-internet.de/bgb/__110.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["bgb-at", "geschaeftsfaehigkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-011",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Anfechtung",
    title: "Eigenschaftsirrtum (§ 119 Abs. 2 BGB) vs. Motivirrtum",
    prompt:
      "Was ist ein Eigenschaftsirrtum nach § 119 Abs. 2 BGB und wie grenzt er sich vom unbeachtlichen Motivirrtum ab?",
    options: [],
    answers: [],
    explanation:
      "§ 119 Abs. 2 BGB: Als Irrtum über den Inhalt der Erklärung gilt auch der Irrtum über solche Eigenschaften der Person oder Sache, die im Verkehr als wesentlich angesehen werden (verkehrswesentliche Eigenschaften). Wesentliche Eigenschaften sind dauerhaft mit der Person oder Sache verbundene Merkmale, die nach der Verkehrsanschauung für ihre Beurteilung maßgeblich sind (z.B. Echtheit eines Gemäldes, Zahlungsfähigkeit des Schuldners). Bloße Motive (Beweggründe), die der Erklärung vorgelagert sind und den Inhalt der WE nicht betreffen — z.B. irrtümliche Annahme, die Ware werde günstig weiterzuverkaufen sein — sind grundsätzlich unbeachtlich.",
    sourceIds: ["bgb119"],
    sources: [
      { id: "bgb119", label: "§ 119 BGB", url: "https://www.gesetze-im-internet.de/bgb/__119.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["bgb-at", "anfechtung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-012",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Anfechtung",
    title: "Arglistige Täuschung und widerrechtliche Drohung (§ 123 BGB)",
    prompt:
      "Welche Voraussetzungen müssen für eine Anfechtung wegen arglistiger Täuschung bzw. widerrechtlicher Drohung (§ 123 BGB) vorliegen?",
    options: [],
    answers: [],
    explanation:
      "§ 123 Abs. 1 BGB: Arglistige Täuschung — jemand erregt oder bestärkt einen Irrtum durch bewusstes Vorspiegeln oder Unterdrücken von Tatsachen, um die Abgabe der WE zu veranlassen (Vorsatz hinsichtlich Täuschung und Kausalität erforderlich). Widerrechtliche Drohung — Ankündigung eines Übels, auf dessen Eintritt der Drohende (zumindest scheinbar) Einfluss hat, verbunden mit der Aufforderung zu einem Verhalten; die Drohung muss widerrechtlich sein (Mittel-Zweck-Relation). § 123 Abs. 2 BGB: Bei Täuschung durch einen Dritten ist Anfechtung nur möglich, wenn der Erklärungsempfänger die Täuschung kannte oder kennen musste.",
    sourceIds: ["bgb123"],
    sources: [
      { id: "bgb123", label: "§ 123 BGB", url: "https://www.gesetze-im-internet.de/bgb/__123.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["bgb-at", "anfechtung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-013",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Anfechtung",
    title: "Anfechtungsfristen: § 121 vs. § 124 BGB",
    prompt:
      "Welche Anfechtungsfristen gelten bei Irrtum (§ 119 BGB) und bei arglistiger Täuschung/Drohung (§ 123 BGB)?",
    options: [],
    answers: [],
    explanation:
      "§ 121 Abs. 1 BGB (Irrtumsanfechtung): Die Anfechtung muss ohne schuldhaftes Zögern (unverzüglich) nach Kenntnis vom Anfechtungsgrund erfolgen. Die Anfechtung ist ausgeschlossen, wenn seit der Abgabe der Willenserklärung zehn Jahre verstrichen sind (§ 121 Abs. 2 BGB). § 124 Abs. 1 BGB (Täuschung/Drohung): Die Anfechtungsfrist beträgt ein Jahr. Bei Täuschung beginnt die Frist mit dem Zeitpunkt, in dem der Anfechtungsberechtigte die Täuschung entdeckt; bei Drohung mit dem Zeitpunkt, in dem die Zwangslage aufgehört hat (§ 124 Abs. 2 BGB). Absolute Ausschlussfrist: zehn Jahre (§ 124 Abs. 3 BGB).",
    sourceIds: ["bgb121", "bgb124"],
    sources: [
      { id: "bgb121", label: "§ 121 BGB", url: "https://www.gesetze-im-internet.de/bgb/__121.html" },
      { id: "bgb124", label: "§ 124 BGB", url: "https://www.gesetze-im-internet.de/bgb/__124.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["bgb-at", "anfechtung"],
    reviewStatus: "beta-ready",
  },

  // reviewed (Re-Gate final 2026-06-03): § 143 Abs. 1-4-Zuordnung korrekt.
  {
    id: "hj-card-014",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Anfechtung",
    title: "Anfechtungserklärung und Anfechtungsgegner (§ 143 BGB)",
    prompt:
      "Gegenüber wem ist die Anfechtungserklärung nach § 143 BGB abzugeben?",
    options: [],
    answers: [],
    explanation:
      "§ 143 Abs. 1 BGB: Die Anfechtung erfolgt durch Erklärung gegenüber dem Anfechtungsgegner. § 143 Abs. 2 BGB: Bei einem Vertrag ist Anfechtungsgegner der andere Teil (im Fall des § 123 Abs. 2 S. 2 derjenige, der aus dem Vertrag unmittelbar ein Recht erworben hat). § 143 Abs. 3 BGB: Bei einem einseitigen Rechtsgeschäft, das einem anderen gegenüber vorzunehmen war, ist dieser andere der Anfechtungsgegner — das gilt auch, wenn das Rechtsgeschäft einer Behörde gegenüber vorzunehmen war (z.B. Kündigung: der Gekündigte). § 143 Abs. 4 BGB: In den übrigen Fällen ist Anfechtungsgegner jeder, der aufgrund des Rechtsgeschäfts unmittelbar einen rechtlichen Vorteil erlangt hat; war die Willenserklärung einer Behörde gegenüber abzugeben, kann die Anfechtung auch ihr gegenüber erklärt werden. Die Anfechtung letztwilliger Verfügungen (Testament) richtet sich demgegenüber nach den erbrechtlichen Sondervorschriften §§ 2078 ff. BGB (Erklärung gegenüber dem Nachlassgericht, § 2081 BGB), nicht nach § 143 BGB. Die Anfechtungserklärung ist empfangsbedürftig und formfrei.",
    sourceIds: ["bgb143"],
    sources: [
      { id: "bgb143", label: "§ 143 BGB", url: "https://www.gesetze-im-internet.de/bgb/__143.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["bgb-at", "anfechtung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-015",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Anfechtung",
    title: "Vertrauensschaden (§ 122 BGB)",
    prompt:
      "Welche Haftungsfolge sieht § 122 BGB für den Anfechtenden vor und was umfasst das negative Interesse?",
    options: [],
    answers: [],
    explanation:
      "§ 122 Abs. 1 BGB: Ist eine Willenserklärung nach §§ 118, 119 BGB nichtig oder anfechtbar und wird sie angefochten, so hat der Erklärende dem Empfänger sowie Dritten den Schaden zu ersetzen, den diese dadurch erlitten haben, dass sie auf die Gültigkeit der Erklärung vertraut haben — jedoch nicht über den Betrag des Interesses hinaus, das der Empfänger an der Gültigkeit hatte. Das negative Interesse (Vertrauensschaden) umfasst alle Aufwendungen und entgangenen Chancen, die dem Geschädigten entstanden sind, weil er auf die Wirksamkeit des Geschäfts vertraute (z.B. Vertragsabschlusskosten, entgangene anderweitige Vertragsabschlüsse). Es ist kein Erfüllungsanspruch (= positives Interesse). § 122 Abs. 2 BGB: Keine Ersatzpflicht, wenn der Verletzte den Grund der Nichtigkeit oder Anfechtbarkeit kannte oder kennen musste.",
    sourceIds: ["bgb122"],
    sources: [
      { id: "bgb122", label: "§ 122 BGB", url: "https://www.gesetze-im-internet.de/bgb/__122.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["bgb-at", "anfechtung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-016",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Auslegung",
    title: "Objektiver Empfängerhorizont (§§ 133, 157 BGB)",
    prompt:
      "Was bedeutet 'objektiver Empfängerhorizont' als Auslegungsmaßstab und auf welchen Normen beruht er?",
    options: [],
    answers: [],
    explanation:
      "§ 133 BGB: Bei der Auslegung einer Willenserklärung ist der wirkliche Wille zu erforschen und nicht an dem buchstäblichen Sinne des Ausdrucks zu haften. § 157 BGB: Verträge sind so auszulegen, wie Treu und Glauben mit Rücksicht auf die Verkehrssitte es erfordern. Der objektive Empfängerhorizont ist der Maßstab für empfangsbedürftige Willenserklärungen: Maßgeblich ist, wie ein verständiger, redlicher Empfänger in der Lage des konkreten Empfängers die Erklärung nach Wortlaut und Begleitumständen verstehen durfte und musste. Der tatsächliche innere Wille des Erklärenden ist grundsätzlich unerheblich, soweit er nicht für den Empfänger erkennbar war.",
    sourceIds: ["bgb133", "bgb157"],
    sources: [
      { id: "bgb133", label: "§ 133 BGB", url: "https://www.gesetze-im-internet.de/bgb/__133.html" },
      { id: "bgb157", label: "§ 157 BGB", url: "https://www.gesetze-im-internet.de/bgb/__157.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["bgb-at", "auslegung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-017",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Auslegung",
    title: "Falsa demonstratio non nocet",
    prompt:
      "Was besagt der Grundsatz 'falsa demonstratio non nocet' und auf welche BGB-Norm stützt er sich?",
    options: [],
    answers: [],
    explanation:
      "Falsa demonstratio non nocet ('eine falsche Bezeichnung schadet nicht'): Haben beide Parteien übereinstimmend dasselbe gemeint, gilt das übereinstimmend Gewollte, auch wenn sie sich eines objektiv falschen Ausdrucks bedient haben. Gesetzliche Grundlage: § 133 BGB (erforsche den wirklichen Willen). Klassisches Schulbeispiel (RGZ 99, 147 — 'Haakjöringsköd'): Beide Parteien bezeichneten Walfleisch als 'Haakjöringsköd' (norweg. eigentlich Haifischfleisch); der Vertrag geht auf Walfleisch. Der Grundsatz geht dem Anfechtungsrecht vor: Weil keine Diskrepanz zwischen Wille und Erklärung besteht, ist kein Raum für § 119 BGB.",
    sourceIds: ["bgb133"],
    sources: [
      { id: "bgb133", label: "§ 133 BGB", url: "https://www.gesetze-im-internet.de/bgb/__133.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["bgb-at", "auslegung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-018",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Form",
    title: "Formnichtigkeit und Formzwecke (§ 125 BGB)",
    prompt:
      "Was ordnet § 125 BGB bei Nichtbeachtung der gesetzlichen Form an, und welche Formzwecke kennt man?",
    options: [],
    answers: [],
    explanation:
      "§ 125 S. 1 BGB: Ein Rechtsgeschäft, das der durch Gesetz vorgeschriebenen Form ermangelt, ist nichtig. § 125 S. 2 BGB: Der Mangel der durch Rechtsgeschäft bestimmten Form hat im Zweifel ebenfalls Nichtigkeit zur Folge. Klassische Formzwecke: (1) Warnfunktion — Schutz des Erklärenden vor übereilten Entschlüssen (z.B. § 766 BGB Bürgschaft), (2) Beweisfunktion — Sicherung des Inhalts der Erklärung (z.B. § 550 BGB Mietvertrag), (3) Beratungsfunktion/Begleitungsfunktion — Hinzuziehung einer neutralen Person (z.B. §§ 311b, 925 BGB notarielle Beurkundung bei Grundstücksübertragung), (4) Publizitätsfunktion — Offenkundigkeit gegenüber Dritten.",
    sourceIds: ["bgb125"],
    sources: [
      { id: "bgb125", label: "§ 125 BGB", url: "https://www.gesetze-im-internet.de/bgb/__125.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["bgb-at", "form"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-019",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Nichtigkeit",
    title: "Verstoß gegen ein gesetzliches Verbot (§ 134 BGB)",
    prompt:
      "Wann ist ein Rechtsgeschäft nach § 134 BGB nichtig, und was gilt, wenn das Verbot nur eine Partei trifft?",
    options: [],
    answers: [],
    explanation:
      "§ 134 BGB: Ein Rechtsgeschäft, das gegen ein gesetzliches Verbot verstößt, ist nichtig, wenn sich nicht aus dem Gesetz ein anderes ergibt. Entscheidend ist der Schutzzweck (Normzweck) des Verbots: Richtet sich das Verbot gegen das Rechtsgeschäft als solches (beiderseitiges Verbotsgesetz), tritt Nichtigkeit ein. Richtet sich das Verbot nur gegen eine Partei (einseitiges Verbotsgesetz, z.B. Schwarzarbeit durch Handwerker: § 1 Abs. 2 Nr. 2 SchwarzArbG), ist anhand des Normzwecks zu entscheiden, ob Nichtigkeit auch das Geschäft insgesamt erfasst oder nur die verbotswidrig handelnde Partei sanktioniert werden soll (vgl. BGH NJW 2013, 1957).",
    sourceIds: ["bgb134"],
    sources: [
      { id: "bgb134", label: "§ 134 BGB", url: "https://www.gesetze-im-internet.de/bgb/__134.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Subsumtion",
    skillTags: ["bgb-at", "nichtigkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-020",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Nichtigkeit",
    title: "Sittenwidrigkeit und Wucher (§ 138 BGB)",
    prompt:
      "Was unterscheidet die allgemeine Sittenwidrigkeit (§ 138 Abs. 1 BGB) von Wucher (§ 138 Abs. 2 BGB)?",
    options: [],
    answers: [],
    explanation:
      "§ 138 Abs. 1 BGB: Ein Rechtsgeschäft, das gegen die guten Sitten verstößt, ist nichtig ('Anstandsgefühl aller billig und gerecht Denkenden'). Maßgeblich ist eine wertende Gesamtbetrachtung aus Inhalt, Motiv und Zweck. Ein subjektives Element (Kenntnis der sittenwidrigen Umstände) ist jedenfalls bei Vermögensverschiebungen zu prüfen. § 138 Abs. 2 BGB (Wucher): Nichtig ist ein Rechtsgeschäft, durch das jemand unter Ausbeutung der Zwangslage, der Unerfahrenheit, des Mangels an Urteilsvermögen oder der erheblichen Willensschwäche eines anderen sich oder einem Dritten für eine Leistung Vermögensvorteile versprechen oder gewähren lässt, die in einem auffälligen Missverhältnis zu der Leistung stehen. Wucher erfordert also: (1) objektives Element: auffälliges Missverhältnis von Leistung und Gegenleistung; (2) subjektives Element: Ausbeutung einer der genannten Schwächelagen.",
    sourceIds: ["bgb138"],
    sources: [
      { id: "bgb138", label: "§ 138 BGB", url: "https://www.gesetze-im-internet.de/bgb/__138.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["bgb-at", "nichtigkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-021",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Nichtigkeit",
    title: "Teilnichtigkeit (§ 139 BGB)",
    prompt:
      "Was besagt § 139 BGB zur Teilnichtigkeit und welche Auslegungsregel enthält er?",
    options: [],
    answers: [],
    explanation:
      "§ 139 BGB: Ist ein Teil eines Rechtsgeschäfts nichtig, so ist das ganze Rechtsgeschäft nichtig, wenn nicht anzunehmen ist, dass es auch ohne den nichtigen Teil vorgenommen sein würde. Es handelt sich um eine widerlegliche Vermutung zugunsten der Gesamtnichtigkeit. Der hypothetische Parteiwille entscheidet: Hätten die Parteien das Rechtsgeschäft auch ohne den nichtigen Teil vorgenommen, bleibt der Rest wirksam. Beispiel: Unwirksame AGB-Klausel (§ 306 BGB enthält lex specialis zu § 139 BGB für AGB-Fälle). § 139 BGB ist dispositiv und kann durch Salvatorische Klausel abbedungen werden.",
    sourceIds: ["bgb139"],
    sources: [
      { id: "bgb139", label: "§ 139 BGB", url: "https://www.gesetze-im-internet.de/bgb/__139.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["bgb-at", "nichtigkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-022",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Nichtigkeit",
    title: "Umdeutung (§ 140 BGB)",
    prompt:
      "Unter welchen Voraussetzungen kann ein nichtiges Rechtsgeschäft nach § 140 BGB umgedeutet werden?",
    options: [],
    answers: [],
    explanation:
      "§ 140 BGB: Entspricht ein nichtiges Rechtsgeschäft den Erfordernissen eines anderen Rechtsgeschäfts, so gilt das letztere, wenn anzunehmen ist, dass dessen Geltung bei Kenntnis der Nichtigkeit gewollt sein würde. Voraussetzungen: (1) Das Rechtsgeschäft ist nichtig; (2) es erfüllt die Voraussetzungen eines anderen (formell oder inhaltlich); (3) nach dem hypothetischen Parteiwillen hätten die Parteien das andere Rechtsgeschäft gewollt, wenn sie die Nichtigkeit gekannt hätten. Beispiel: Formnichtige Bürgschaft (§ 766 BGB) kann als Schuldmitübernahme (§ 415 BGB) umzudeuten sein, wenn die Voraussetzungen vorliegen. § 140 BGB ist eng auszulegen und tritt hinter § 139 BGB zurück.",
    sourceIds: ["bgb140"],
    sources: [
      { id: "bgb140", label: "§ 140 BGB", url: "https://www.gesetze-im-internet.de/bgb/__140.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["bgb-at", "nichtigkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-023",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Bedingung",
    title: "Aufschiebende vs. auflösende Bedingung (§ 158 BGB)",
    prompt:
      "Was unterscheidet aufschiebende und auflösende Bedingung nach § 158 BGB?",
    options: [],
    answers: [],
    explanation:
      "§ 158 Abs. 1 BGB (aufschiebende Bedingung — Suspensivbedingung): Das Rechtsgeschäft tritt erst mit Eintritt des ungewissen zukünftigen Ereignisses in Wirksamkeit. Bis dahin besteht eine Anwartschaft des Berechtigten. § 158 Abs. 2 BGB (auflösende Bedingung — Resolutivbedingung): Das Rechtsgeschäft ist sofort wirksam; mit Eintritt der Bedingung endet die Wirksamkeit. Beide Varianten setzen ein zukünftiges, ungewisses Ereignis voraus. Abgrenzung zur Befristung (§ 163 BGB): Bei der Befristung steht der Eintritt des Ereignisses (= gewisses zukünftiges Ereignis) fest; nur der Zeitpunkt ist ggf. ungewiss. Anwartschaft bei aufschiebender Bedingung: § 160 BGB schützt die Rechtsposition des Erwerbers vor Beeinträchtigungen durch den Veräußerer.",
    sourceIds: ["bgb158"],
    sources: [
      { id: "bgb158", label: "§ 158 BGB", url: "https://www.gesetze-im-internet.de/bgb/__158.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Abgrenzung",
    skillTags: ["bgb-at", "bedingung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-024",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Bedingung",
    title: "Treuwidrige Vereitelung/Herbeiführung der Bedingung (§ 162 BGB)",
    prompt:
      "Was ordnet § 162 BGB an, wenn eine Partei den Bedingungseintritt treuwidrig vereitelt oder herbeiführt?",
    options: [],
    answers: [],
    explanation:
      "§ 162 Abs. 1 BGB: Wird der Eintritt der Bedingung von der Partei, zu deren Nachteil er gereichen würde, wider Treu und Glauben verhindert, so gilt die Bedingung als eingetreten. § 162 Abs. 2 BGB: Wird der Eintritt der Bedingung von der Partei, zu deren Vorteil er gereicht, wider Treu und Glauben herbeigeführt, so gilt die Bedingung als nicht eingetreten. Voraussetzung ist jeweils treuwidriges (= gegen § 242 BGB verstoßendes) Verhalten; leichte Fahrlässigkeit genügt nicht. Rechtsgedanke: Niemand darf aus eigenem Treuverstoß Vorteile ziehen (venire contra factum proprium).",
    sourceIds: ["bgb162"],
    sources: [
      { id: "bgb162", label: "§ 162 BGB", url: "https://www.gesetze-im-internet.de/bgb/__162.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Rechtsfolge",
    skillTags: ["bgb-at", "bedingung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-025",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Bedingung",
    title: "Befristung (§ 163 BGB) — Abgrenzung zur Bedingung",
    prompt:
      "Was unterscheidet die Befristung (§ 163 BGB) von der Bedingung (§ 158 BGB)?",
    options: [],
    answers: [],
    explanation:
      "§ 163 BGB: Ist für die Wirkung eines Rechtsgeschäfts bei einem Anfangstermin ein Zeitpunkt bestimmt, so finden auf den Anfangstermin die für die aufschiebende Bedingung, auf den Endtermin die für die auflösende Bedingung geltenden Vorschriften entsprechende Anwendung. Kernunterschied: Bei der Bedingung (§ 158 BGB) ist das Ereignis ungewiss — es steht nicht fest, ob es eintreten wird. Bei der Befristung (§ 163 BGB) ist das Ereignis gewiss — es steht fest, dass der Zeitpunkt eintreten wird; lediglich der genaue Zeitpunkt kann noch ungewiss sein (z.B. Tod einer Person). Praktische Folge: § 163 BGB verweist auf die §§ 158 ff. BGB, sodass Anwartschaftsschutz und § 162 BGB analog gelten.",
    sourceIds: ["bgb163"],
    sources: [
      { id: "bgb163", label: "§ 163 BGB", url: "https://www.gesetze-im-internet.de/bgb/__163.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["bgb-at", "bedingung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-026",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Verjährung",
    title: "Regelverjährung und Verjährungsbeginn (§§ 195, 199 BGB)",
    prompt:
      "Wie lange beträgt die Regelverjährungsfrist (§ 195 BGB) und wann beginnt sie (§ 199 Abs. 1 BGB)?",
    options: [],
    answers: [],
    explanation:
      "§ 195 BGB: Die regelmäßige Verjährungsfrist beträgt drei Jahre. § 199 Abs. 1 BGB: Die Frist beginnt mit dem Schluss des Jahres, in dem (1) der Anspruch entstanden ist und (2) der Gläubiger von den den Anspruch begründenden Umständen und der Person des Schuldners Kenntnis erlangt oder ohne grobe Fahrlässigkeit erlangen müsste. Beide Voraussetzungen (Entstehung + subjektives Element) müssen kumulativ vorliegen. Das subjektive Element ist auf das Ende des Jahres zu projizieren: Entscheidend ist, ob am 31. Dezember des betreffenden Jahres Kenntnis vorlag; die Frist läuft dann ab 1. Januar des folgenden Jahres.",
    sourceIds: ["bgb195", "bgb199"],
    sources: [
      { id: "bgb195", label: "§ 195 BGB", url: "https://www.gesetze-im-internet.de/bgb/__195.html" },
      { id: "bgb199", label: "§ 199 BGB", url: "https://www.gesetze-im-internet.de/bgb/__199.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["bgb-at", "verjaehrung"],
    reviewStatus: "beta-ready",
  },

  // reviewed (Re-Gate 2026-06-03): Korrektur bestätigt.
  {
    id: "hj-card-027",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Verjährung",
    title: "Wirkung der Verjährung (§ 214 BGB)",
    prompt:
      "Welche Wirkung hat die eingetretene Verjährung nach § 214 BGB — erlischt der Anspruch?",
    options: [],
    answers: [],
    explanation:
      "§ 214 Abs. 1 BGB: Nach Eintritt der Verjährung ist der Schuldner berechtigt, die Leistung zu verweigern. Die Verjährung begründet damit eine dauerhafte Einrede (Leistungsverweigerungsrecht), lässt den Anspruch aber nicht erlöschen. Der Anspruch selbst besteht fort; er kann nur nicht mehr erfolgreich gerichtlich durchgesetzt werden, sofern der Schuldner die Einrede erhebt. § 214 Abs. 2 S. 1 BGB: Das zur Befriedigung eines verjährten Anspruchs Geleistete kann nicht zurückgefordert werden — auch wenn in Unkenntnis der Verjährung geleistet wurde. Abgrenzung zu Ausschlussfristen (z.B. den Anfechtungsfristen § 121 Abs. 2 und § 124 BGB oder der Erklärungsfrist § 626 Abs. 2 BGB): Diese lassen das Recht mit Fristablauf endgültig erlöschen und sind als Einwendung von Amts wegen zu berücksichtigen — anders als die Verjährung, die den Anspruch nur einredeweise lähmt.",
    sourceIds: ["bgb214"],
    sources: [
      { id: "bgb214", label: "§ 214 BGB", url: "https://www.gesetze-im-internet.de/bgb/__214.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["bgb-at", "verjaehrung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-028",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Vertragsschluss",
    title: "Zugang unter Abwesenden und Widerruf (§ 130 BGB)",
    prompt:
      "Wann geht eine Willenserklärung unter Abwesenden zu (§ 130 Abs. 1 S. 1 BGB) und wie kann sie widerrufen werden (§ 130 Abs. 1 S. 2 BGB)?",
    options: [],
    answers: [],
    explanation:
      "§ 130 Abs. 1 S. 1 BGB: Eine Willenserklärung unter Abwesenden wird wirksam, wenn sie dem anderen zugeht. Zugang liegt vor, wenn die Erklärung so in den Machtbereich des Empfängers gelangt, dass dieser unter normalen Verhältnissen die Möglichkeit hat, von ihr Kenntnis zu nehmen (Einwurf ins Postfach bis zur üblichen Leerungszeit). § 130 Abs. 1 S. 2 BGB: Die Wirksamkeit der Erklärung wird nicht dadurch beeinträchtigt, dass der Erklärende vor oder gleichzeitig mit ihr einen Widerruf an den Empfänger absendet; der Widerruf muss jedoch gleichzeitig oder vor der Erklärung zugehen, um sie zu vernichten. Ist der Widerruf später zugegangen, entfaltet er keine Wirkung.",
    sourceIds: ["bgb130"],
    sources: [
      { id: "bgb130", label: "§ 130 BGB", url: "https://www.gesetze-im-internet.de/bgb/__130.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["bgb-at", "vertragsschluss"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-029",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Vertragsschluss",
    title: "Bindung an den Antrag und Annahmefristen (§§ 145–147 BGB)",
    prompt:
      "An welchen Antrag ist der Antragende gebunden (§ 145 BGB) und welche Annahmefristen gelten (§§ 146, 147 BGB)?",
    options: [],
    answers: [],
    explanation:
      "§ 145 BGB: Wer einem anderen die Schließung eines Vertrags anträgt, ist an den Antrag gebunden, es sei denn, er hat die Gebundenheit ausgeschlossen. § 146 BGB: Der Antrag erlischt, wenn er dem Antragenden gegenüber abgelehnt wird oder nicht rechtzeitig angenommen wird. § 147 Abs. 1 BGB (Annahme unter Anwesenden): Der einem Anwesenden gegenüber gemachte Antrag kann nur sofort angenommen werden. § 147 Abs. 2 BGB (Annahme unter Abwesenden): Der einem Abwesenden gegenüber gemachte Antrag kann nur bis zu dem Zeitpunkt angenommen werden, in welchem der Antragende den Eingang der Antwort unter regelmäßigen Umständen erwarten darf. Verspätete Annahme gilt als neuer Antrag (§ 150 Abs. 1 BGB).",
    sourceIds: ["bgb145", "bgb147"],
    sources: [
      { id: "bgb145", label: "§ 145 BGB", url: "https://www.gesetze-im-internet.de/bgb/__145.html" },
      { id: "bgb147", label: "§ 147 BGB", url: "https://www.gesetze-im-internet.de/bgb/__147.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["bgb-at", "vertragsschluss"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-card-030",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "BGB AT - Vertragsschluss",
    title: "Offener und versteckter Dissens (§§ 154, 155 BGB)",
    prompt:
      "Was unterscheidet den offenen Dissens (§ 154 BGB) vom versteckten Dissens (§ 155 BGB)?",
    options: [],
    answers: [],
    explanation:
      "§ 154 Abs. 1 S. 1 BGB (offener/bewusster Dissens): Solange die Parteien sich nicht über alle Punkte eines Vertrags geeinigt haben, über die nach der Erklärung auch nur einer Partei eine Einigung erforderlich ist, ist im Zweifel der Vertrag nicht geschlossen. Die Parteien wissen, dass keine Einigung erzielt wurde. § 155 BGB (versteckter/unbewusster Dissens): Haben sich die Parteien über einen Punkt, über den eine Einigung erforderlich war, stillschweigend keine Einigung erzielt und hätten sie bei Kenntnis des Dissenses den Vertrag nicht geschlossen, ist der Vertrag im Zweifel nicht zustande gekommen. Unterschied: Beim offenen Dissens ist das Fehlen der Einigung den Parteien bekannt; beim versteckten Dissens glauben die Parteien, sich geeinigt zu haben, ohne es in Wirklichkeit getan zu haben.",
    sourceIds: ["bgb154", "bgb155"],
    sources: [
      { id: "bgb154", label: "§ 154 BGB", url: "https://www.gesetze-im-internet.de/bgb/__154.html" },
      { id: "bgb155", label: "§ 155 BGB", url: "https://www.gesetze-im-internet.de/bgb/__155.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["bgb-at", "vertragsschluss"],
    reviewStatus: "beta-ready",
  },

  // ─── ORDERINGS hj-order-003 … hj-order-007 ───────────────────────────────

  {
    id: "hj-order-003",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "ordering",
    topic: "BGB AT - Anspruchsprüfung",
    title: "Dreischritt der Anspruchsprüfung",
    prompt: "Bringe die drei Prüfungsstufen des juristischen Anspruchsaufbaus in die richtige Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Anspruch entstanden (Anspruchsgrundlage + Tatbestandsvoraussetzungen erfüllt)",
      "Anspruch nicht erloschen (Erlöschensgründe, z.B. Erfüllung § 362, Aufrechnung § 387, Anfechtung § 142)",
      "Anspruch durchsetzbar (keine Einreden, z.B. Verjährung § 214, § 320 BGB)",
    ],
    explanation:
      "Der klassische Drei-Stufen-Aufbau der Anspruchsprüfung: (1) Anspruch entstanden — die Anspruchsgrundlage muss in allen Tatbestandsmerkmalen erfüllt sein; (2) Anspruch nicht erloschen — Erlöschensgründe (§ 362: Erfüllung, § 387: Aufrechnung, § 397: Erlass, § 142: Anfechtung ex tunc) negieren; (3) Anspruch durchsetzbar — dauernde oder aufschiebende Einreden prüfen (§ 214 Verjährungseinrede, § 320 Einrede des nicht erfüllten Vertrags). Fehlt eine Stufe, besteht kein durchsetzbarer Anspruch.",
    sourceIds: ["bgb194"],
    sources: [
      { id: "bgb194", label: "§ 194 BGB", url: "https://www.gesetze-im-internet.de/bgb/__194.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Schema",
    skillTags: ["bgb-at", "vertragsschluss"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-order-004",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "ordering",
    topic: "BGB AT - Geschäftsfähigkeit",
    title: "Prüfschema Minderjährigenrecht",
    prompt: "Ordne die Prüfungsschritte für ein Rechtsgeschäft eines Minderjährigen in der gutachterlichen Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Geschäftsfähigkeit des Minderjährigen bestimmen: unter 7 Jahre (§ 104 Nr. 1) → geschäftsunfähig; 7–17 Jahre (§ 106) → beschränkt geschäftsfähig",
      "Ist das Geschäft lediglich rechtlich vorteilhaft? (§ 107 BGB) → dann allein wirksam",
      "Liegt Einwilligung (vorherige Zustimmung) des gesetzlichen Vertreters vor? (§ 107 BGB) → dann wirksam",
      "Ohne Einwilligung: schwebende Unwirksamkeit bis zur Genehmigung (§ 108 Abs. 1 BGB) oder Verweigerung",
      "Hilfsweise: Sind die Mittel vom Vertreter überlassen und vollständig bewirkt? (§ 110 BGB) → dann wirksam",
    ],
    explanation:
      "Das Minderjährigenrecht folgt einer klaren Kaskade: Zunächst die Geschäftsfähigkeitsstufe bestimmen (§§ 104, 106 BGB), dann den Ausnahmetatbestand 'lediglich rechtlicher Vorteil' prüfen (§ 107 BGB), dann die Einwilligung des gesetzlichen Vertreters (§ 107 BGB). Fehlt die Einwilligung, folgt die schwebende Unwirksamkeit mit Genehmigungsmöglichkeit (§ 108 BGB). Zuletzt der Taschengeldparagraph (§ 110 BGB) als eigenständige Wirksamkeitsvoraussetzung.",
    sourceIds: ["bgb104", "bgb106", "bgb107", "bgb108", "bgb110"],
    sources: [
      { id: "bgb104", label: "§ 104 BGB", url: "https://www.gesetze-im-internet.de/bgb/__104.html" },
      { id: "bgb106", label: "§ 106 BGB", url: "https://www.gesetze-im-internet.de/bgb/__106.html" },
      { id: "bgb107", label: "§ 107 BGB", url: "https://www.gesetze-im-internet.de/bgb/__107.html" },
      { id: "bgb108", label: "§ 108 BGB", url: "https://www.gesetze-im-internet.de/bgb/__108.html" },
      { id: "bgb110", label: "§ 110 BGB", url: "https://www.gesetze-im-internet.de/bgb/__110.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["bgb-at", "geschaeftsfaehigkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-order-005",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "ordering",
    topic: "BGB AT - Abstraktionsprinzip",
    title: "Trennungs- und Abstraktionsprinzip im Kauffall",
    prompt:
      "Ordne die rechtlichen Schritte und Konsequenzen bei einem gescheiterten Kaufvertrag in die richtige Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Verpflichtungsgeschäft: Abschluss des Kaufvertrags (§ 433 BGB) — schuldrechtliche Ebene",
      "Verfügungsgeschäft: Übereignung der Kaufsache (§ 929 BGB) — dingliche Ebene",
      "Getrennte Wirksamkeitsprüfung: Nichtigkeit des Kaufvertrags berührt die Übereignung grundsätzlich nicht (Abstraktionsprinzip)",
      "Rückabwicklung über Bereicherungsrecht: Kondiktionsanspruch gemäß § 812 Abs. 1 S. 1 Alt. 1 BGB (Leistungskondiktion)",
    ],
    explanation:
      "Trennungsprinzip: Kaufvertrag (§ 433 BGB) und Übereignung (§ 929 BGB) sind zwei eigenständige Rechtsgeschäfte. Abstraktionsprinzip: Ihre Wirksamkeit ist voneinander unabhängig — ist der Kaufvertrag nichtig (z.B. §§ 134, 138, 125 BGB), bleibt die Übereignung wirksam; der Eigentümer wechselt dennoch. Die gestörte Causa (Kaufvertrag) berechtigt zur Rückforderung über § 812 Abs. 1 S. 1 Alt. 1 BGB (Leistung ohne Rechtsgrund). Diese Konstruktion ist typisch für das deutsche Sachenrecht und eine häufige Prüfungsfrage.",
    sourceIds: ["bgb433", "bgb812"],
    sources: [
      { id: "bgb433", label: "§ 433 BGB", url: "https://www.gesetze-im-internet.de/bgb/__433.html" },
      { id: "bgb812", label: "§ 812 BGB", url: "https://www.gesetze-im-internet.de/bgb/__812.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["bgb-at", "vertragsschluss"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-order-006",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "ordering",
    topic: "BGB AT - Vertragsschluss",
    title: "Prüfschema Vertragsschluss",
    prompt:
      "Ordne die Prüfungsschritte für den Abschluss eines wirksamen Vertrags in der gutachterlichen Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Antrag: wirksame empfangsbedürftige Willenserklärung mit hinreichend bestimmtem Inhalt (§ 145 BGB)",
      "Wirksame Annahme: inhaltlich deckungsgleich, rechtzeitig (§§ 147–149 BGB), vorbehaltlos",
      "Konsens: keine Abweichung von Antrag und Annahme (kein Dissens §§ 154, 155 BGB)",
    ],
    explanation:
      "Ein Vertrag kommt durch zwei übereinstimmende Willenserklärungen zustande: Antrag (Offerte) und Annahme. § 145 BGB verpflichtet den Antragenden zur Bindung. Die Annahme muss inhaltlich exakt mit dem Antrag übereinstimmen (§ 150 Abs. 2 BGB: Erweiterungen oder Einschränkungen gelten als Ablehnung + neuer Antrag). Offener Dissens (§ 154 BGB) oder versteckter Dissens (§ 155 BGB) verhindern das Zustandekommen. Zusätzlich sind Wirksamkeitshindernisse (Geschäftsfähigkeit, Form, §§ 134, 138 BGB) gesondert zu prüfen.",
    sourceIds: ["bgb145", "bgb147"],
    sources: [
      { id: "bgb145", label: "§ 145 BGB", url: "https://www.gesetze-im-internet.de/bgb/__145.html" },
      { id: "bgb147", label: "§ 147 BGB", url: "https://www.gesetze-im-internet.de/bgb/__147.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["bgb-at", "vertragsschluss"],
    reviewStatus: "beta-ready",
  },

  // FIX (Reviewer 2026-06-03): reviewed (Reihenfolge eindeutig korrekt). Aber Normangabe im 3. orderItem ungenau: "Vollmacht (§ 166 Abs. 2, § 167 BGB)" — die Vollmachtserteilung ist § 167 BGB; § 166 Abs. 2 BGB betrifft die Wissenszurechnung bei weisungsgebundener Vollmacht, nicht die Begründung der Vollmacht. Autor: "§ 166 Abs. 2" hier streichen, nur § 167 BGB belassen.
  {
    id: "hj-order-007",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "ordering",
    topic: "BGB AT - Stellvertretung",
    title: "Prüfschema Stellvertretung",
    prompt:
      "Ordne die Prüfungsschritte einer rechtsgeschäftlichen Stellvertretung in die gutachterliche Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Eigene Willenserklärung des Vertreters (kein Bote): Vertreter gestaltet den Inhalt selbst",
      "Handeln in fremdem Namen / Offenkundigkeit (§ 164 Abs. 1 BGB): Vertretungsabsicht erkennbar",
      "Vertretungsmacht: Vollmacht (§ 167 BGB), gesetzliche Vertretung oder durch Rechtsschein",
      "Bei fehlender Vertretungsmacht: schwebend unwirksam, Genehmigung des Vertretenen (§ 177 BGB) oder Haftung des Vertreters ohne Vertretungsmacht (§ 179 BGB)",
    ],
    explanation:
      "Das viergliedrige Schema der Stellvertretung: (1) Eigene WE des Vertreters — unterscheidet ihn vom Boten (§ 120 BGB analog). (2) Offenkundigkeitsprinzip nach § 164 Abs. 1 BGB; fehlt es, trifft das Geschäft den Vertreter selbst (§ 164 Abs. 2 BGB). (3) Vertretungsmacht durch Vollmacht (§ 167 BGB), gesetzliche Vertretung oder Rechtsscheinsvollmacht (§§ 170–173 BGB). (4) Fehlt die Vertretungsmacht: § 177 BGB (schwebende Unwirksamkeit, Genehmigung möglich) oder § 179 BGB (Haftung des falsus procurator). Missbrauch der Vertretungsmacht und § 181 BGB (Insichgeschäft) als zusätzliche Schranken.",
    sourceIds: ["bgb164", "bgb167", "bgb177", "bgb179"],
    sources: [
      { id: "bgb164", label: "§ 164 BGB", url: "https://www.gesetze-im-internet.de/bgb/__164.html" },
      { id: "bgb167", label: "§ 167 BGB", url: "https://www.gesetze-im-internet.de/bgb/__167.html" },
      { id: "bgb177", label: "§ 177 BGB", url: "https://www.gesetze-im-internet.de/bgb/__177.html" },
      { id: "bgb179", label: "§ 179 BGB", url: "https://www.gesetze-im-internet.de/bgb/__179.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["bgb-at", "stellvertretung"],
    reviewStatus: "beta-ready",
  },

  // ─── CLOZES hj-cloze-003 … hj-cloze-008 ────────────────────────────────

  {
    id: "hj-cloze-003",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "cloze",
    topic: "BGB AT - Anfechtung",
    title: "Anfechtungsfrist bei Täuschung/Drohung (§ 124 BGB)",
    prompt:
      "Die Anfechtung wegen arglistiger Täuschung oder widerrechtlicher Drohung muss nach § 124 Abs. 1 BGB binnen ___ erfolgen.",
    options: ["unverzüglich", "einem Jahr", "drei Jahren", "zwei Wochen"],
    answers: [1],
    explanation:
      "§ 124 Abs. 1 BGB: Die Anfechtungsfrist bei Täuschung oder Drohung beträgt ein Jahr. Sie beginnt nach § 124 Abs. 2 BGB bei Täuschung mit Entdeckung, bei Drohung mit dem Aufhören der Zwangslage. Abgrenzung: § 121 BGB (Irrtumsanfechtung) verlangt unverzügliches Handeln; § 195 BGB (Regelverjährung) ist keine Anfechtungsfrist.",
    sourceIds: ["bgb124"],
    sources: [
      { id: "bgb124", label: "§ 124 BGB", url: "https://www.gesetze-im-internet.de/bgb/__124.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Lücke",
    skillTags: ["bgb-at", "anfechtung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-cloze-004",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "cloze",
    topic: "BGB AT - Verjährung",
    title: "Regelverjährungsfrist (§ 195 BGB)",
    prompt:
      "Die regelmäßige Verjährungsfrist beträgt nach § 195 BGB ___.",
    options: ["ein Jahr", "zwei Jahre", "drei Jahre", "zehn Jahre"],
    answers: [2],
    explanation:
      "§ 195 BGB: Die regelmäßige Verjährungsfrist beträgt drei Jahre. Sie gilt subsidiär für alle Ansprüche, für die keine Sonderverjährung (z.B. § 438 BGB zwei Jahre bei Sachmängelansprüchen, § 548 BGB sechs Monate bei Miete, § 196 BGB zehn Jahre bei Grundstücksrechten) eingreift.",
    sourceIds: ["bgb195"],
    sources: [
      { id: "bgb195", label: "§ 195 BGB", url: "https://www.gesetze-im-internet.de/bgb/__195.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Lücke",
    skillTags: ["bgb-at", "verjaehrung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-cloze-005",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "cloze",
    topic: "BGB AT - Verjährung",
    title: "Beginn der Regelverjährung (§ 199 Abs. 1 BGB)",
    prompt:
      "Die Regelverjährungsfrist beginnt nach § 199 Abs. 1 BGB mit dem Schluss des ___, in dem der Anspruch entstanden ist und der Gläubiger Kenntnis erlangt oder grob fahrlässig nicht erlangt hat.",
    options: ["Monats", "Quartals", "Jahres", "Tages"],
    answers: [2],
    explanation:
      "§ 199 Abs. 1 BGB: Der Lauf der dreijährigen Regelverjährung beginnt mit dem Schluss des Jahres (= 31. Dezember), in dem (Nr. 1) der Anspruch entstanden ist und (Nr. 2) der Gläubiger von den anspruchsbegründenden Umständen und der Person des Schuldners Kenntnis erlangt oder ohne grobe Fahrlässigkeit erlangen müsste. Die Jahresendformel hat eine vereinfachende Wirkung: Alle Ansprüche desselben Jahres verjähren gleichzeitig am Ende des dritten Folgejahres.",
    sourceIds: ["bgb199"],
    sources: [
      { id: "bgb199", label: "§ 199 BGB", url: "https://www.gesetze-im-internet.de/bgb/__199.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Lücke",
    skillTags: ["bgb-at", "verjaehrung"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-cloze-006",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "cloze",
    topic: "BGB AT - Geschäftsfähigkeit",
    title: "Altersgrenze Geschäftsunfähigkeit (§ 104 Nr. 1 BGB)",
    prompt:
      "Geschäftsunfähig ist nach § 104 Nr. 1 BGB, wer das ___ Lebensjahr nicht vollendet hat.",
    options: ["dritte", "fünfte", "siebte", "sechste"],
    answers: [2],
    explanation:
      "§ 104 Nr. 1 BGB: Geschäftsunfähig ist, wer nicht das siebte Lebensjahr vollendet hat. Kinder unter 7 Jahren können keine wirksamen Willenserklärungen abgeben (§ 105 Abs. 1 BGB). Ab dem vollendeten 7. bis zum vollendeten 17. Lebensjahr gilt die beschränkte Geschäftsfähigkeit (§ 106 BGB).",
    sourceIds: ["bgb104"],
    sources: [
      { id: "bgb104", label: "§ 104 BGB", url: "https://www.gesetze-im-internet.de/bgb/__104.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Lücke",
    skillTags: ["bgb-at", "geschaeftsfaehigkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-cloze-007",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "cloze",
    topic: "BGB AT - Geschäftsfähigkeit",
    title: "Genehmigungsfrist nach Aufforderung (§ 108 Abs. 2 S. 2 BGB)",
    prompt:
      "Nach Aufforderung des anderen Teils kann die Genehmigung nach § 108 Abs. 2 S. 2 BGB nur bis zum Ablauf von ___ nach Empfang der Aufforderung erklärt werden.",
    options: ["einer Woche", "zwei Wochen", "einem Monat", "drei Werktagen"],
    answers: [1],
    explanation:
      "§ 108 Abs. 2 S. 2 BGB: Die Genehmigung des gesetzlichen Vertreters kann nach Aufforderung durch den anderen Teil nur innerhalb von zwei Wochen ab Empfang der Aufforderung erklärt werden; wird sie nicht erklärt, gilt sie als verweigert. Die Frist beginnt mit Zugang der Aufforderung beim gesetzlichen Vertreter (§ 187 Abs. 1 BGB analoge Berechnung). Die Zweiwochenfrist dient dem Verkehrsschutz: Der andere Teil soll Klarheit erhalten.",
    sourceIds: ["bgb108"],
    sources: [
      { id: "bgb108", label: "§ 108 BGB", url: "https://www.gesetze-im-internet.de/bgb/__108.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Lücke",
    skillTags: ["bgb-at", "geschaeftsfaehigkeit"],
    reviewStatus: "beta-ready",
  },

  {
    id: "hj-cloze-008",
    pathId: "hagen-jura",
    mode: "ausbildung",
    kind: "cloze",
    topic: "BGB AT - Vertragsschluss",
    title: "Annahmefrist bei Antrag unter Abwesenden (§ 147 Abs. 2 BGB)",
    prompt:
      "Der einem Abwesenden gegenüber gemachte Antrag kann nach § 147 Abs. 2 BGB nur bis zu dem Zeitpunkt angenommen werden, in welchem der Antragende den Eingang der Antwort ___ erwarten darf.",
    options: [
      "innerhalb von zwei Wochen",
      "unverzüglich nach Absendung",
      "unter regelmäßigen Umständen",
      "nach Ablauf von drei Tagen",
    ],
    answers: [2],
    explanation:
      "§ 147 Abs. 2 BGB: Der Antrag unter Abwesenden erlischt, wenn die Annahme nicht in dem Zeitraum eingeht, in dem der Antragende bei normalem Verlauf der Umstände (Beförderungsweg + angemessene Überlegungszeit) mit der Antwort rechnen kann. Das Gesetz nennt 'regelmäßige Umstände' als Maßstab — kein fester Tageszeitraum, sondern eine auf den Einzelfall bezogene Zeitspanne. Eine verspätete Annahme gilt nach § 150 Abs. 1 BGB als neuer Antrag.",
    sourceIds: ["bgb147"],
    sources: [
      { id: "bgb147", label: "§ 147 BGB", url: "https://www.gesetze-im-internet.de/bgb/__147.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Lücke",
    skillTags: ["bgb-at", "vertragsschluss"],
    reviewStatus: "beta-ready",
  },
];
